import { ICropping } from "../../fields"
import { arrayMax, except, isArrayEmpty, selectMany, sum } from "../../utils/array"
import { checkDateIntersection } from "../../utils/date"
import { isArray, sortDates } from "../../utils/utils"
import { getCroppingDatesInYear } from "../history"
import { TimelineItem } from "./layout"

interface Unit {
    nodes: ICropping[]
    start: Date
    end: Date
    area: number
    next: Unit[]
    prev: Unit[]
}

interface Container {

    units: Unit[]
    next: Container[]

    index: number
    height: number

}

export function createTimelineItems3(nodes: ICropping[], nextSelector: (n: ICropping) => ICropping[], prevSelector: (n: ICropping) => ICropping[]): TimelineItem[] {


    const units = getUnits(nodes, nextSelector, prevSelector)

    const containers = setupContainers(units)

    const items = containers.map((c, index) => {

        const group = index + 1

        return [createContainerItems(c, group)].concat(
            flattenContainers(c.next).map(c => createContainerItems(c, group))
        )

    })

    return flatten(items) as TimelineItem[]

    ///

    function createContainerItems(container: Container, group: number) {

        const rowSpan = container.height / container.units.length

        return selectMany(container.units.map((u, index) => u.nodes.map(c => {

            return {

                node: c,

                group,
                rowIndex: container.index + (index * rowSpan),
                rowSpan,

                next: [],
                prev: [],
                rankLengthFromPrev: null,
                rankLengthFromNext: null

            }

        })))

    }

    function flattenContainers(containers: Container[]): Container[] {
        return containers.concat(selectMany(containers.map(c => flattenContainers(c.next))))
    }

    function flatten(a) {
        return selectMany(a.map(i => isArray(i) ? flatten(i) : i))
    }

}

function setupContainers(units: Unit[]) {

    const containers = distributeUnits(units)

    containers.forEach(c => {
        setHeights(c, 0)
    })

    containers.forEach(c => {
        setIndices(c, 0)
    })

    return containers

    ///

    function setHeights(container: Container, current: number) {

        const { units, next } = container

        let height = Math.max(units.length, current)
        let nextHeight = 1

        if (next.length) {

            nextHeight = Math.max(1, height / next.length)

            container.next.forEach(n => setHeights(n, nextHeight))

            nextHeight = sum(next, n => n.height)

        }

        height = Math.max(height, nextHeight)

        container.height = height

        return height

    }

    function setIndices(container: Container, index: number) {

        container.next.forEach(next => {

            next.index = index

            index = isArrayEmpty(next.next)
                ? index + next.height
                : setIndices(next, index)

        })

        return index

    }

}

function distributeUnits(units: Unit[]): Container[] {

    if (isArrayEmpty(units)) return []

    const groups = splitUnrelatedUnits(units)

    return groups.map(group => {

        group = sortByStart(group)

        const concurrent = sortByArea(getConcurrentUnits(group))

        const others = sortByArea(except(group, concurrent))

        const next = distributeUnits(others)

        return {
            units: concurrent,
            next,
            height: 1,
            index: 0
        }
    })

}

/**
 * Returns a subset of @units that have concurrent dates, starting from those of the
 * first units in the array
 * *
 * @units is expected to be sorted by starting date
 */
export function getConcurrentUnits(units: Unit[]) {

    const first = units[0]

    const state = {
        group: [first],
        start: first.start,
        end: first.end
    }

    const result = units.slice(1).reduce((s, next) => {

        if (checkDateIntersection(s.start, s.end, next.start, next.end, true)) {
            s.group.push(next)
            s.end = s.end > next.end ? s.end : next.end
        }

        return s

    }, state)

    return result.group

}

export function splitUnrelatedUnits(units: Unit[]) {

    let q = units.slice()
    let groups = [] as Unit[][]

    while (q.length) {
        const group = getRelatedUnits(q[0])
        groups.push(group)
        q = except(q, group)
    }

    return groups

    ///

    function filter(u: Unit) {
        return units.indexOf(u) >= 0
    }

    function getRelatedUnits(unit: Unit) {

        let q = [unit]
        let index = 0

        while (index < q.length) {

            const rels = q[index].next.concat(q[index].prev)

            rels.filter(filter).forEach(r => {
                if (q.indexOf(r) < 0) q.push(r)
            })

            index++
        }

        return q

    }

}

export function getUnits(nodes: ICropping[], nextSelector: (n: ICropping) => ICropping[], prevSelector: (n: ICropping) => ICropping[]): Unit[] {

    const getDates = getCroppingDatesInYear

    const units = groupUnits(nodes, nextSelector, prevSelector)
        .map(n => ({
            nodes: n,
            start: getDates(n[0]).start,
            end: getDates(n[n.length - 1]).end,
            area: arrayMax(n, c => c.Area),
            prev: [],
            next: []
        }))

    function mapNext(c: ICropping[]) {
        return c.map(i => units.find(a => a.nodes[0] == i)).filter(i => !!i)
    }

    function mapPrev(c: ICropping[]) {
        return c.map(i => units.find(a => a.nodes[a.nodes.length - 1] == i)).filter(i => !!i)
    }

    units.forEach(u => {
        u.next = mapNext(nextSelector(u.nodes[u.nodes.length - 1]))
        u.prev = mapPrev(prevSelector(u.nodes[0]))
    })

    return units

}

export function groupUnits<T>(units: T[], nextSelector: (n: T) => T[], prevSelector: (n: T) => T[]): T[][] {

    const groups = units
        .filter(isFirst)
        .map(group)
        .filter(g => g.length > 1)

    const others = except(units, selectMany(groups))

    return groups.concat(others.map(o => [o]))

    ///

    function isFirst(u: T) {
        const prev = prevSelector(u)
        if (prev.length == 0) return true
        if (prev.length != 1) return false
        return (nextSelector(prev[0]).length != 1)
    }

    function group(u: T): T[] {

        const next = nextSelector(u)

        if (next.length == 0) return [u]
        if (next.length != 1) return []

        const single = next[0]

        return prevSelector(single).length == 1
            ? [u].concat(group(single))
            : [u]

    }

}

function sortByStart(units: Unit[]) {
    return units.sort((a, b) => sortDates(a.start, b.start))
}

function sortByArea(units: Unit[]) {
    return units.sort((a, b) => (b.area - a.area))
}

export function unitToString(u: Unit) {
    return u.nodes.map(n => n.CroppingId).join("-")
}

export function unitsToString(u: Unit[]) {
    return u.map(unitToString).join(",")
}
